import { createContext, useContext, useState, useEffect } from 'react';

const SensoryContext = createContext();

export const useSensory = () => {
  const context = useContext(SensoryContext);
  if (!context) {
    throw new Error('useSensory must be used within a SensoryProvider');
  }
  return context;
};

export const SensoryProvider = ({ children }) => {
  const [isLowSensory, setIsLowSensory] = useState(() => {
    const saved = localStorage.getItem('lowSensory');
    return saved === 'true';
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return saved === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('fontSize') || 'medium';
  });

  const [dyslexicFont, setDyslexicFont] = useState(() => {
    return localStorage.getItem('dyslexicFont') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('lowSensory', isLowSensory);
    if (isLowSensory) {
      document.documentElement.classList.add('low-sensory');
    } else {
      document.documentElement.classList.remove('low-sensory');
    }
  }, [isLowSensory]);

  useEffect(() => {
    localStorage.setItem('darkMode', isDarkMode);
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize);
    document.documentElement.classList.remove('font-size-small', 'font-size-medium', 'font-size-large', 'font-size-xlarge');
    document.documentElement.classList.add(`font-size-${fontSize}`);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('dyslexicFont', dyslexicFont);
    if (dyslexicFont) {
      document.documentElement.classList.add('dyslexic-font');
    } else {
      document.documentElement.classList.remove('dyslexic-font');
    }
  }, [dyslexicFont]);

  const toggleLowSensory = () => setIsLowSensory(prev => !prev);
  const toggleDarkMode = () => setIsDarkMode(prev => !prev);
  const toggleDyslexicFont = () => setDyslexicFont(prev => !prev);

  return (
    <SensoryContext.Provider value={{
      isLowSensory,
      toggleLowSensory,
      isDarkMode,
      toggleDarkMode,
      fontSize,
      setFontSize,
      dyslexicFont,
      toggleDyslexicFont
    }}>
      {children}
    </SensoryContext.Provider>
  );
};
